import React, { RefObject, useRef, useState, ReactNode } from 'react';

import { $Values } from 'utility-types';
import { asStyledComponent } from '@cld/component-enhancer';
import { Layout, ORIENTATION } from '../constants';
import { Indicator } from './Indicator';
import { TabsContext, TabsContextType } from './TabsContext';
import { useTabsIndicatorCalculations } from './useTabsIndicatorCalculations';
import { Wrapper } from './Tabs.styled';

export interface TabsProps {
  activeTabId: TabsContextType['activeTabId'];
  variant?: TabsContextType['variant'];
  ariaLabel?: string;
  layout?: Layout;
  orientation?: $Values<typeof ORIENTATION>;
  children?: ReactNode;
}

export const Tabs = asStyledComponent(
  ({ activeTabId, ariaLabel, children,
     layout = Layout.STRETCH, orientation = ORIENTATION.HORIZONTAL, variant = 'solid', ...rest }: TabsProps
  ) => {
    const [activeTabRef, setActiveTabRef] = useState<RefObject<HTMLDivElement> | null>(null);
    const wrapperRef = useRef(null);

    const isVertical = orientation === ORIENTATION.VERTICAL;

    const { indicatorPosition, indicatorSize } = useTabsIndicatorCalculations(wrapperRef, activeTabRef, isVertical);

    return (
      <TabsContext.Provider value={{ activeTabId, isVertical, setActiveTabRef, layout, variant }}>
        <Wrapper aria-label={ariaLabel} layout={layout} isVertical={isVertical} ref={wrapperRef} role="tablist" {...rest}>
          {children}

          {activeTabId !== undefined && (
            <Indicator
              indicatorPosition={indicatorPosition}
              indicatorSize={indicatorSize}
              isVertical={isVertical}
            />
          )}
        </Wrapper>
      </TabsContext.Provider>
    );
  });
